import { Controller } from "@hotwired/stimulus"
import Swiper from 'swiper'
import { Pagination } from 'swiper/modules'

// Connects to data-controller="swiper-board"
// SwiperとSortableJSを共存させるボード用コントローラー
export default class extends Controller {
  static targets = ["container", "wrapper", "pagination"]

  connect() {
    this.initSwiper()
    this.setupDragScrolling()
  }

  disconnect() {
    this.swiper?.destroy()
    this.removeDragScrolling()
  }

  initSwiper() {
    this.swiper = new Swiper(this.containerTarget, {
      modules: [Pagination],
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 8,
      // // add-task-areaではスワイプ無効
      // noSwiping: true,
      // noSwipingSelector: '.add-task-area',
      watchSlidesProgress: true,
      touchRatio: 1,
      threshold: 5,
      touchAngle: 30, // 水平な動きのみスワイプと認識（リスト内縦スクロールを許可）
      pagination: {
        el: this.hasPaginationTarget ? this.paginationTarget : null,
        clickable: true,
      },
    })
  }

  setupDragScrolling() {
    this.isDragging = false
    this.dragScrollRafId = null

    // SortableJSのドラッグ開始/終了を検知
    this.boundHandleSortStart = this.handleSortStart.bind(this)
    this.boundHandleSortEnd = this.handleSortEnd.bind(this)
    document.addEventListener('dragstart', this.boundHandleSortStart)
    document.addEventListener('dragend', this.boundHandleSortEnd)
    document.addEventListener('drop', this.boundHandleSortEnd)

    // マウスドラッグ用
    this.boundHandleDragOver = this.handleDragOver.bind(this)
    this.containerTarget.addEventListener('dragover', this.boundHandleDragOver)

    // タッチドラッグ用
    this.boundHandleTouchStart = this.handleTouchStart.bind(this)
    this.boundHandleTouchMove = this.handleTouchMove.bind(this)
    this.boundHandleTouchEnd = this.handleTouchEnd.bind(this)
    this.containerTarget.addEventListener('touchstart', this.boundHandleTouchStart, { passive: true })
    this.containerTarget.addEventListener('touchmove', this.boundHandleTouchMove, { passive: false })
    document.addEventListener('touchend', this.boundHandleTouchEnd)
    document.addEventListener('touchcancel', this.boundHandleTouchEnd)
  }

  removeDragScrolling() {
    document.removeEventListener('dragstart', this.boundHandleSortStart)
    document.removeEventListener('dragend', this.boundHandleSortEnd)
    document.removeEventListener('drop', this.boundHandleSortEnd)
    this.containerTarget?.removeEventListener('dragover', this.boundHandleDragOver)
    this.containerTarget?.removeEventListener('touchstart', this.boundHandleTouchStart)
    this.containerTarget?.removeEventListener('touchmove', this.boundHandleTouchMove)
    document.removeEventListener('touchend', this.boundHandleTouchEnd)
    document.removeEventListener('touchcancel', this.boundHandleTouchEnd)
    this.stopDragScroll()
  }

  handleSortStart(event) {
    // SortableJSのカードがドラッグされたかチェック
    if (event.target.closest('[data-task-id]')) {
      this.isDragging = true
      // ドラッグ中はSwiperのタッチ操作を無効化
      this.swiper.allowTouchMove = false
    }
  }

  handleSortEnd(event) {
    if (!this.isDragging) return

    this.isDragging = false
    this.stopDragScroll()
    // Swiperのタッチ操作を再有効化
    this.swiper.allowTouchMove = true

    // ドロップ先のスライドを中央に表示
    const dropTarget = event.target?.closest('.swiper-slide')
    if (dropTarget) {
      this.slideToElement(dropTarget)
    }
  }

  handleDragOver(event) {
    if (!this.isDragging) return
    this.scrollByPosition(event.clientX)
  }

  handleTouchStart(event) {
    // タッチ開始位置を記録（SortableJSのドラッグ検知用）
    this.touchStartTarget = event.target
  }

  handleTouchMove(event) {
    // SortableJSがドラッグ中かどうかを確認
    const draggingElement = document.querySelector('.sortable-drag, .sortable-ghost, .sortable-fallback')
    if (!draggingElement) return

    this.isDragging = true
    this.swiper.allowTouchMove = false

    const touch = event.touches[0]
    if (touch) {
      this.scrollByPosition(touch.clientX)
    }
  }

  handleTouchEnd() {
    if (!this.isDragging) return

    this.isDragging = false
    this.stopDragScroll()
    this.swiper.allowTouchMove = true

    // 現在表示されているスライドの中で最も中央に近いものをアクティブに
    requestAnimationFrame(() => {
      this.swiper.updateSlides()
      this.swiper.slideTo(this.swiper.activeIndex)
    })
  }

  scrollByPosition(clientX) {
    const margin = 60
    const rect = this.containerTarget.getBoundingClientRect()
    const scrollSpeed = 8

    // 左端に近い場合
    if (clientX < rect.left + margin) {
      const intensity = 1 - (clientX - rect.left) / margin
      this.currentScrollSpeed = -scrollSpeed * intensity
      this.startDragScroll()
    }
    // 右端に近い場合
    else if (clientX > rect.right - margin) {
      const intensity = 1 - (rect.right - clientX) / margin
      this.currentScrollSpeed = scrollSpeed * intensity
      this.startDragScroll()
    }
    else {
      this.stopDragScroll()
    }
  }

  startDragScroll() {
    // 既にスクロール中なら速度だけ更新済みなのでリターン
    if (this.dragScrollRafId) return

    const scroll = () => {
      // Swiperのtranslateを直接操作してスクロール
      const currentTranslate = this.swiper.translate
      const newTranslate = currentTranslate - this.currentScrollSpeed

      // スクロール範囲の制限
      const minTranslate = this.swiper.minTranslate()
      const maxTranslate = this.swiper.maxTranslate()
      const clampedTranslate = Math.max(maxTranslate, Math.min(minTranslate, newTranslate))

      this.swiper.setTranslate(clampedTranslate)
      this.swiper.updateProgress()
      this.swiper.updateActiveIndex()

      this.dragScrollRafId = requestAnimationFrame(scroll)
    }

    this.dragScrollRafId = requestAnimationFrame(scroll)
  }

  stopDragScroll() {
    if (this.dragScrollRafId) {
      cancelAnimationFrame(this.dragScrollRafId)
      this.dragScrollRafId = null
    }
    this.currentScrollSpeed = 0
  }

  slideToElement(element) {
    const slides = Array.from(this.wrapperTarget.querySelectorAll('.swiper-slide'))
    const slideIndex = slides.indexOf(element)
    if (slideIndex >= 0) {
      this.swiper.slideTo(slideIndex, 300)
    }
  }
}
